// ---------------------------------------------------------------------------
// MarkdownEditor — editor de texto livre estilo GitHub.
// ---------------------------------------------------------------------------
// Duas abas: Visualizar (react-markdown com remark-gfm para listas,
// tabelas, code, etc) e Editar (textarea monospace + toolbar). A aba
// Visualizar fica primeiro e e' a selecao padrao (``initialMode``).
// Toolbar insere sintaxe markdown em torno da seleção atual do textarea.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

import Edit from "@material-ui/icons/Edit";
import Visibility from "@material-ui/icons/Visibility";
import FormatQuote from "@material-ui/icons/FormatQuote";
import Code from "@material-ui/icons/Code";
import LinkIcon from "@material-ui/icons/Link";
import FormatListBulleted from "@material-ui/icons/FormatListBulleted";
import FormatListNumbered from "@material-ui/icons/FormatListNumbered";
import CheckBox from "@material-ui/icons/CheckBox";
import InfoOutlined from "@material-ui/icons/InfoOutlined";

import useDrawerStyles from "./styles";
import useThemeColors from "../../../../hooks/useThemeColors";

const MARKDOWN_REMARK_PLUGINS = [remarkGfm, remarkBreaks];

const MarkdownEditor = ({
  value,
  onChange,
  onSave,
  onCancel,
  initialMode = "preview",
  placeholder,
  forceWriteKey,
  disabled = false,
}) => {
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (forceWriteKey != null) {
      setMode("write");
    }
  }, [forceWriteKey]);

  // Wrappers de sintaxe aplicados em torno da seleção atual.
  // Quando não há seleção, é inserido um texto placeholder localizado.
  // ``useMemo`` para evitar re-criar o dict a cada render (o ``t`` e' estavel
  // dentro do mesmo ciclo de idioma).
  const WRAPS = useMemo(
    () => ({
      bold: {
        before: "**",
        after: "**",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_bold"),
      },
      italic: {
        before: "_",
        after: "_",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_italic"),
      },
      code: {
        before: "`",
        after: "`",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_code"),
      },
      quote: {
        before: "> ",
        after: "",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_quote"),
        block: true,
      },
      heading: {
        before: "## ",
        after: "",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_heading"),
        block: true,
      },
      link: {
        before: "[",
        after: "](https://)",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_link_text"),
      },
      bullet: {
        before: "- ",
        after: "",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_list_item"),
        block: true,
      },
      numbered: {
        before: "1. ",
        after: "",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_list_item"),
        block: true,
      },
      task: {
        before: "- [ ] ",
        after: "",
        placeholder: t("modeling_inputs_v3.markdown_editor.ph_task"),
        block: true,
      },
    }),
    [t],
  );
  const effectivePlaceholder =
    placeholder || t("modeling_inputs_v3.markdown_editor.ph_default");

  const applyWrap = (type) => {
    if (disabled) return;
    const spec = WRAPS[type];
    if (!spec) return;
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end) || spec.placeholder;
    const prefix = value.substring(0, start);
    const suffix = value.substring(end);
    // Para blocos (lista, título, citação), garante que estamos no
    // começo de uma linha; caso contrário quebra uma linha antes.
    let insertion;
    if (spec.block) {
      const needsBreak = prefix.length > 0 && !prefix.endsWith("\n");
      insertion = `${needsBreak ? "\n" : ""}${spec.before}${selected}${spec.after}`;
    } else {
      insertion = `${spec.before}${selected}${spec.after}`;
    }
    const newVal = prefix + insertion + suffix;
    onChange(newVal);
    // Após o React aplicar o novo value, reposiciona cursor.
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const caretPos =
        start +
        (spec.block && !prefix.endsWith("\n") && prefix.length > 0 ? 1 : 0) +
        spec.before.length +
        selected.length;
      textareaRef.current.setSelectionRange(caretPos, caretPos);
      textareaRef.current.focus();
    });
  };

  return (
    <div
      className={classes.mdEditor}
      style={
        disabled
          ? { opacity: 0.55, pointerEvents: "none", userSelect: "none" }
          : undefined
      }
      aria-disabled={disabled || undefined}
    >
      <div className={classes.mdTabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "preview"}
          className={`${classes.mdTab} ${
            mode === "preview" ? classes.mdTabActive : ""
          }`}
          onClick={() => setMode("preview")}
          disabled={disabled}
        >
          <Visibility /> {t("modeling_inputs_v3.markdown_editor.tab_preview")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "write"}
          className={`${classes.mdTab} ${
            mode === "write" ? classes.mdTabActive : ""
          }`}
          onClick={() => setMode("write")}
          disabled={disabled}
        >
          <Edit /> {t("modeling_inputs_v3.markdown_editor.tab_edit")}
        </button>
      </div>

      {mode === "write" ? (
        <>
          <div className={classes.mdToolbar}>
            <button
              type="button"
              className={`${classes.mdBtn} ${classes.mdBtnHeading}`}
              onClick={() => applyWrap("heading")}
              title={t("modeling_inputs_v3.markdown_editor.tt_heading")}
            >
              H
            </button>
            <button
              type="button"
              className={`${classes.mdBtn} ${classes.mdBtnBold}`}
              onClick={() => applyWrap("bold")}
              title={t("modeling_inputs_v3.markdown_editor.tt_bold")}
            >
              B
            </button>
            <button
              type="button"
              className={`${classes.mdBtn} ${classes.mdBtnItalic}`}
              onClick={() => applyWrap("italic")}
              title={t("modeling_inputs_v3.markdown_editor.tt_italic")}
            >
              I
            </button>
            <span className={classes.mdSep} />
            <button
              type="button"
              className={classes.mdBtn}
              onClick={() => applyWrap("quote")}
              title={t("modeling_inputs_v3.markdown_editor.tt_quote")}
            >
              <FormatQuote />
            </button>
            <button
              type="button"
              className={classes.mdBtn}
              onClick={() => applyWrap("code")}
              title={t("modeling_inputs_v3.markdown_editor.tt_code")}
            >
              <Code />
            </button>
            <button
              type="button"
              className={classes.mdBtn}
              onClick={() => applyWrap("link")}
              title={t("modeling_inputs_v3.markdown_editor.tt_link")}
            >
              <LinkIcon />
            </button>
            <span className={classes.mdSep} />
            <button
              type="button"
              className={classes.mdBtn}
              onClick={() => applyWrap("bullet")}
              title={t("modeling_inputs_v3.markdown_editor.tt_list")}
            >
              <FormatListBulleted />
            </button>
            <button
              type="button"
              className={classes.mdBtn}
              onClick={() => applyWrap("numbered")}
              title={t("modeling_inputs_v3.markdown_editor.tt_ordered")}
            >
              <FormatListNumbered />
            </button>
            <button
              type="button"
              className={classes.mdBtn}
              onClick={() => applyWrap("task")}
              title={t("modeling_inputs_v3.markdown_editor.tt_task")}
            >
              <CheckBox />
            </button>
          </div>
          <textarea
            ref={textareaRef}
            className={classes.mdTextarea}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={effectivePlaceholder}
            spellCheck="false"
            readOnly={disabled}
          />
          <div className={classes.mdEditorFooter}>
            <span className={classes.mdHintInline}>
              <InfoOutlined style={{ fontSize: 13 }} />
              {t("modeling_inputs_v3.markdown_editor.syntax_hint_title")}
            </span>
            <span className={classes.mdHintInline}>
              <code>**{t("modeling_inputs_v3.markdown_editor.ph_bold")}**</code>
            </span>
            <span className={classes.mdHintInline}>
              <code>_{t("modeling_inputs_v3.markdown_editor.ph_italic")}_</code>
            </span>
            <span className={classes.mdHintInline}>
              <code>`{t("modeling_inputs_v3.markdown_editor.ph_code")}`</code>
            </span>
            <div className={classes.mdSpacer} />
            {onCancel && (
              <button
                type="button"
                className={`${classes.btn} ${classes.btnGhost} ${classes.btnSmall}`}
                onClick={onCancel}
              >
                {t("modeling_inputs_v3.markdown_editor.cancel")}
              </button>
            )}
            {onSave && (
              <button
                type="button"
                className={`${classes.btn} ${classes.btnPrimary} ${classes.btnSmall}`}
                onClick={onSave}
              >
                {t("modeling_inputs_v3.markdown_editor.save")}
              </button>
            )}
          </div>
        </>
      ) : (
        <div className={classes.mdPreviewPane}>
          {value.trim().length > 0 ? (
            <ReactMarkdown remarkPlugins={MARKDOWN_REMARK_PLUGINS}>
              {value}
            </ReactMarkdown>
          ) : (
            <span style={{ color: "#999", fontStyle: "italic" }}>
              {t("modeling_inputs_v3.markdown_editor.empty_preview")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

MarkdownEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  initialMode: PropTypes.oneOf(["write", "preview"]),
  placeholder: PropTypes.string,
  forceWriteKey: PropTypes.number,
  disabled: PropTypes.bool,
};

export default MarkdownEditor;
